import {
	CalendarClosingCounts,
	CalendarOccupancyCounts,
	CalendarRequisitionCounts,
} from './calendar.model';
import { Injectable } from '@angular/core';
import { Client } from '@models/clients';
import { Matter, MatterFileType } from '@models/matter';
import { ClientsRepoService } from '@repos/clients/clients.service';
import { MatterClientRelationshipRepoService } from '@repos/matter-client-relationship/matterClientRelRepo.service';
import { MatterRepo } from '@repos/matter/matter.repo';
import isSameDay from 'date-fns/isSameDay';
import { Observable, of, timer } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

export interface CountResponseData {
	[MatterFileType.PURCHASE]: number;
	[MatterFileType.REFINANCE]: number;
	[MatterFileType.SALE]: number;
	dates: string[];
	label: 'closings' | 'requisitions' | 'occupancies';
}

export interface HeaderCountsResponse {
	closings: CalendarClosingCounts;
	requisitions: CalendarRequisitionCounts;
	occupancies: CalendarOccupancyCounts;
	occupancyDates: number[];
	closingDates: number[];
	requisitionDates: number[];
}

@Injectable({
	providedIn: 'root',
})
export class CalendarService {
	today$!: Observable<Date>;

	constructor(
		private matterRepo: MatterRepo,
		private matterClientsRepo: MatterClientRelationshipRepoService,
		private clientsRepo: ClientsRepoService,
	) {
		this.today$ = timer(
			new Date(new Date(new Date()).setHours(24, 0, 0, 0)),
			1000 * 60 * 60, // solves for savings time issues
		).pipe(
			startWith(null),
			map(() => new Date()),
		);
	}

	isToday(date: Date): Observable<boolean> {
		return this.today$.pipe(map((today) => isSameDay(date, today)));
	}

	matterById(matterId: string): Observable<Matter> {
		return matterId ? this.matterRepo.GetMatterById(matterId) : of(null);
	}

	matterClientsByMatterId(matterId: string): Observable<Client[]> {
		if (!matterId) return of([]);

		return this.matterClientsRepo.clientsByMatterId(matterId).pipe(
			map((clients) =>
				clients
					.filter((client) => client.client)
					.sort((a, b) => (a.primaryContact ? -1 : b.primaryContact ? 1 : 0))
					.map((client) => client.id),
			),
			switchMap((ids) => this.clientsRepo.clientsByIds(ids)),
		);
	}
}
